/**
 * GrassRenderer — GPU instanced grass blades on grassland terrain.
 *
 * Samples terrain via Fibonacci sphere, filters for grass-eligible areas
 * (low height, low mountainess, low slope, on land), then renders
 * ~1M grass blades in a single instanced draw call.
 *
 * Uses camera-relative rendering for planetary-scale precision.
 */

import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import {
  RADIUS,
  heightAt,
  noise3,
  rawIslandEnvelope,
  smoothstep,
} from './terrain'

// ============================================================================
// Config
// ============================================================================

const GRASS_COUNT = 1_500_000
const SAMPLE_MULTIPLIER = 3 // oversample to fill GRASS_COUNT after filtering

// Grass eligibility thresholds
const MIN_HEIGHT = 0.003     // above sea level
const MAX_HEIGHT = 0.045     // below rock zone
const MAX_MOUNTAINESS = 0.42 // flat to moderate terrain only
const MAX_SLOPE = 0.12       // gentle slopes only
const MIN_ENVELOPE = 0.05    // clearly on land

// ============================================================================
// Terrain helpers (recompute mountainess like terrainGen.ts)
// ============================================================================

function getMountainess(nx: number, ny: number, nz: number): number {
  const dw = 0.10
  const wx = nx + dw * noise3(nx * 3.5 + 7.3, ny * 3.5 + 2.1, nz * 3.5 + 5.9)
  const wy = ny + dw * noise3(nx * 3.5 + 47.1, ny * 3.5 + 31.7, nz * 3.5 + 19.3)
  const wz = nz + dw * noise3(nx * 3.5 + 91.7, ny * 3.5 + 67.3, nz * 3.5 + 43.1)
  const wl = Math.sqrt(wx * wx + wy * wy + wz * wz)
  const px = wx / wl, py = wy / wl, pz = wz / wl
  const rawMtn = noise3(px * 2.0, py * 2.0, pz * 2.0) * 0.5 + 0.5
  return smoothstep(0.28, 0.72, rawMtn)
}

function getSlope(nx: number, ny: number, nz: number): number {
  const h0 = heightAt(nx, ny, nz)
  const eps = 0.0005

  // Two tangent offsets on the unit sphere
  let tx = -nz, ty = 0, tz = nx
  let tl = Math.sqrt(tx * tx + tz * tz)
  if (tl < 0.001) { tx = 0; ty = -nz; tz = ny; tl = Math.sqrt(tx * tx + ty * ty + tz * tz) }
  tx /= tl; ty /= tl; tz /= tl

  const bx = ny * tz - nz * ty, by = nz * tx - nx * tz, bz = nx * ty - ny * tx

  // Sample in 4 directions
  let maxDiff = 0
  for (const [dx, dy, dz] of [[tx, ty, tz], [-tx, -ty, -tz], [bx, by, bz], [-bx, -by, -bz]]) {
    let sx = nx + dx * eps, sy = ny + dy * eps, sz = nz + dz * eps
    const sl = Math.sqrt(sx * sx + sy * sy + sz * sz)
    sx /= sl; sy /= sl; sz /= sl
    const diff = Math.abs(heightAt(sx, sy, sz) - h0) / eps
    if (diff > maxDiff) maxDiff = diff
  }
  return maxDiff
}

// ============================================================================
// Grass blade geometry — 7 verts, 5 tris (matching prototype)
// ============================================================================

function createBladeGeometry(): THREE.InstancedBufferGeometry {
  const verts = new Float32Array([
    -0.04, 0, 0,   0.04, 0, 0,
    -0.015, 0.5, 0, 0.015, 0.5, 0,
    -0.005, 0.85, 0, 0.005, 0.85, 0,
    0, 1, 0,
  ])
  const idx = new Uint16Array([0, 1, 2, 1, 3, 2, 2, 3, 4, 3, 5, 4, 4, 5, 6])

  const geo = new THREE.InstancedBufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(verts, 3))
  geo.setIndex(new THREE.BufferAttribute(idx, 1))
  return geo
}

// ============================================================================
// Sample grass placement positions
// ============================================================================

function sampleGrassPositions(): {
  positions: Float32Array
  normals: Float32Array
  scales: Float32Array
  randoms: Float32Array
  count: number
} {
  const totalSamples = GRASS_COUNT * SAMPLE_MULTIPLIER
  const positions = new Float32Array(GRASS_COUNT * 3)
  const normals = new Float32Array(GRASS_COUNT * 3)
  const scales = new Float32Array(GRASS_COUNT)
  const randoms = new Float32Array(GRASS_COUNT)

  let placed = 0

  // Fibonacci sphere sampling
  const goldenAngle = Math.PI * (3 - Math.sqrt(5))

  for (let i = 0; i < totalSamples && placed < GRASS_COUNT; i++) {
    // Fibonacci base point
    const y = 1 - (2 * i + 1) / totalSamples
    const r = Math.sqrt(1 - y * y)
    const theta = goldenAngle * i
    let nx = r * Math.cos(theta)
    let ny = y
    let nz = r * Math.sin(theta)

    // Jitter to avoid grid patterns
    const jitter = 0.3 / Math.sqrt(totalSamples)
    nx += (pseudoRandom(i * 3) - 0.5) * jitter
    ny += (pseudoRandom(i * 3 + 1) - 0.5) * jitter
    nz += (pseudoRandom(i * 3 + 2) - 0.5) * jitter
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz)
    nx /= len; ny /= len; nz /= len

    // Check: on land?
    const envelope = rawIslandEnvelope(nx, ny, nz)
    if (envelope < MIN_ENVELOPE) continue

    // Check: height in grass range?
    const h = heightAt(nx, ny, nz)
    if (h < MIN_HEIGHT || h > MAX_HEIGHT) continue

    // Check: not mountainous?
    const mtn = getMountainess(nx, ny, nz)
    if (mtn > MAX_MOUNTAINESS) continue

    // Check: not too steep?
    const slope = getSlope(nx, ny, nz)
    if (slope > MAX_SLOPE) continue

    // Probability fade at edges for natural transition
    let prob = 1.0
    if (h < 0.008) prob *= (h - MIN_HEIGHT) / 0.005
    if (h > 0.035) prob *= 1.0 - (h - 0.035) / 0.010
    if (mtn > 0.3) prob *= 1.0 - (mtn - 0.3) / 0.12
    if (pseudoRandom(i * 7 + 5) > prob) continue

    // Place grass blade
    const terrainR = RADIUS * (1 + Math.max(h, 0))

    positions[placed * 3] = nx * terrainR
    positions[placed * 3 + 1] = ny * terrainR
    positions[placed * 3 + 2] = nz * terrainR

    normals[placed * 3] = nx
    normals[placed * 3 + 1] = ny
    normals[placed * 3 + 2] = nz

    // Scale: taller in lush areas, shorter near edges
    const lushness = smoothstep(0.01, 0.03, h) * (1 - mtn * 0.8)
    scales[placed] = (0.6 + pseudoRandom(i * 11 + 3) * 1.4) * lushness
    randoms[placed] = pseudoRandom(i * 13 + 7)

    placed++
  }

  return { positions, normals, scales, randoms, count: placed }
}

function pseudoRandom(seed: number): number {
  let h = (seed * 374761393 + 668265263) | 0
  h = ((h ^ (h >> 13)) * 1103515245) | 0
  return ((h ^ (h >> 16)) >>> 0) / 4294967296
}

// ============================================================================
// Shaders
// ============================================================================

const grassVertexShader = /* glsl */ `
  precision highp float;

  attribute vec3 instancePosition;
  attribute vec3 instanceNormal;
  attribute float instanceScale;
  attribute float instanceRandom;

  uniform float uTime;
  uniform float uRadius;
  uniform vec3 uCameraDir;
  uniform float uCameraAlt;

  varying float vHeight;
  varying float vRandom;
  varying vec3 vWorldNormal;
  varying float vDist;

  mat3 rotFromNormal(vec3 n) {
    vec3 up = abs(n.y) < 0.99 ? vec3(0., 1., 0.) : vec3(1., 0., 0.);
    vec3 t = normalize(cross(up, n));
    vec3 b = cross(n, t);
    return mat3(t, n, b);
  }

  void main() {
    vHeight = position.y;
    vRandom = instanceRandom;
    vWorldNormal = instanceNormal;

    mat3 rot = rotFromNormal(instanceNormal);

    // Random Y-axis rotation per blade
    float a = instanceRandom * 6.2831;
    float ca = cos(a), sa = sin(a);
    mat3 yRot = mat3(ca, 0., sa, 0., 1., 0., -sa, 0., ca);

    vec3 lp = yRot * position;
    lp.x *= 0.4 + instanceRandom * 0.3;
    lp.y *= instanceScale;

    // Wind
    float wp1 = instancePosition.x * 0.003 + instancePosition.z * 0.0025 + uTime * 1.5;
    float wp2 = instancePosition.x * 0.006 - instancePosition.z * 0.0035 + uTime * 2.4;
    float wind = (sin(wp1) * 0.6 + sin(wp2) * 0.3) * position.y * position.y * 0.5;
    lp.x += wind;
    lp.z += wind * 0.4;

    vec3 worldPos = rot * lp + instancePosition;

    // Camera-relative rendering
    vec3 camWorldPos = uCameraDir * (uRadius + uCameraAlt);
    vec3 camRel = worldPos - camWorldPos;

    vDist = length(camRel);

    mat3 viewRot = mat3(modelViewMatrix);
    gl_Position = projectionMatrix * vec4(viewRot * camRel, 1.0);
  }
`

const grassFragmentShader = /* glsl */ `
  precision highp float;

  varying float vHeight;
  varying float vRandom;
  varying vec3 vWorldNormal;
  varying float vDist;

  uniform vec3 uSunDirection;
  uniform float uSunHeight;

  void main() {
    // Day/night
    float sunIntensity = smoothstep(-0.05, 0.3, uSunHeight);

    // Grass colors — base to tip gradient
    vec3 baseDay = mix(vec3(0.05, 0.15, 0.02), vec3(0.08, 0.22, 0.03), vRandom);
    vec3 tipDay = mix(vec3(0.25, 0.55, 0.08), vec3(0.45, 0.7, 0.12), vRandom);
    vec3 dry = vec3(0.55, 0.52, 0.15);
    float dm = smoothstep(0.7, 1.0, vRandom) * 0.4;

    vec3 colDay = mix(baseDay, tipDay, vHeight);
    colDay = mix(colDay, dry, dm * vHeight);

    // Night variant — desaturated dark green
    vec3 baseNight = vec3(0.01, 0.03, 0.01);
    vec3 tipNight = vec3(0.03, 0.06, 0.02);
    vec3 colNight = mix(baseNight, tipNight, vHeight);

    vec3 col = mix(colNight, colDay, sunIntensity);

    // Lighting
    float sun = max(dot(vWorldNormal, normalize(uSunDirection)), 0.0) * 0.5 + 0.5;
    sun = mix(0.3, sun, sunIntensity);
    float ao = mix(0.35, 1.0, vHeight);
    col *= sun * ao;

    // Distance fade
    float fade = 1.0 - smoothstep(800.0, 1500.0, vDist);

    float alpha = smoothstep(0.0, 0.05, vHeight)
                * (1.0 - smoothstep(0.85, 1.0, vHeight) * 0.2)
                * fade;

    if (alpha < 0.01) discard;
    gl_FragColor = vec4(col, alpha);
  }
`

// ============================================================================
// Component
// ============================================================================

interface GrassRendererProps {
  sunPosition?: THREE.Vector3
}

export function GrassRenderer({ sunPosition }: GrassRendererProps) {
  const meshRef = useRef<THREE.Mesh>(null)

  const { geometry, material, count } = useMemo(() => {
    const { positions, normals, scales, randoms, count } = sampleGrassPositions()

    const geo = createBladeGeometry()
    geo.setAttribute('instancePosition', new THREE.InstancedBufferAttribute(positions, 3))
    geo.setAttribute('instanceNormal', new THREE.InstancedBufferAttribute(normals, 3))
    geo.setAttribute('instanceScale', new THREE.InstancedBufferAttribute(scales, 1))
    geo.setAttribute('instanceRandom', new THREE.InstancedBufferAttribute(randoms, 1))
    geo.instanceCount = count

    const mat = new THREE.ShaderMaterial({
      vertexShader: grassVertexShader,
      fragmentShader: grassFragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uRadius: { value: RADIUS },
        uCameraDir: { value: new THREE.Vector3(0, 1, 0) },
        uCameraAlt: { value: 0 },
        uSunDirection: { value: new THREE.Vector3(0.5, 0.3, 0.8).normalize() },
        uSunHeight: { value: 0.3 },
      },
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
    })

    return { geometry: geo, material: mat, count }
  }, [])

  useEffect(() => {
    return () => {
      geometry.dispose()
      material.dispose()
    }
  }, [geometry, material])

  useFrame((state) => {
    const u = material.uniforms
    u.uTime.value = state.clock.elapsedTime

    const camPos = state.camera.position
    const camLen = camPos.length()
    if (camLen > 0) {
      u.uCameraDir.value.set(camPos.x / camLen, camPos.y / camLen, camPos.z / camLen)
      u.uCameraAlt.value = camLen - RADIUS
    }

    if (sunPosition) {
      u.uSunDirection.value.copy(sunPosition).normalize()
      const cd = u.uCameraDir.value
      const sd = u.uSunDirection.value
      u.uSunHeight.value = sd.x * cd.x + sd.y * cd.y + sd.z * cd.z
    }
  })

  if (count === 0) return null

  return (
    <mesh ref={meshRef} geometry={geometry} material={material} frustumCulled={false} />
  )
}
